import * as React from 'react';
import { IPortalPageSectionItem } from 'models/portal/page/portalpagesectionitem';
import * as c from 'models/constants';

import {
	ViewSectionItemArticle,
	ViewSectionItemPoll,
	ViewSectionItemProduct,
	ViewSectionItemProfile,
	ViewSectionItemShop,
} from 'components/PortalPage/SectionItems';
import { IFollowerDto } from 'models/common';
import { ISectionTextElement } from 'models/common/sectiontextelement';
import { IUserPreferences } from 'models/user/userpreferences';
import { IStore } from 'redux/IStore';
import { goToCard } from 'helpers/common';
const { connect } = require('react-redux');

interface IProps {
	portalPageSectionItem: IPortalPageSectionItem;
	isLastItemInRow: boolean;
	isFirstItemInRow?: boolean;
	sectionType?: string;
	isDragging?: boolean;
	followingItems?: IFollowerDto[];
	followItem?: (type: string, id: string) => void;
	unfollowItem?: (type: string, id: string) => void;
	userPreferences?: IUserPreferences;
	dispatch?: any;
}

@connect(
	(state: IStore) => ({
		userPreferences: state.userPreferences,
	}),
	(dispatch) => ({
		dispatch,
	}),
)
class PortalPageSectionItemRenderer extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.goToCard = this.goToCard.bind(this);
	}

	private getFollowType(): string | undefined {
		const { portalPageSectionItem } = this.props;
		switch (portalPageSectionItem.type) {
			case c.PORTAL_PAGE_SECTION_ITEM_PROFILE:
				return 'User';
			case c.PORTAL_PAGE_SECTION_ITEM_SHOP:
				return 'Shop';
			case c.PORTAL_PAGE_SECTION_ITEM_PRODUCT:
				return 'Product';
			default:
				return undefined;
		}
	}

	private getSectionText(index: number | undefined): string | undefined {
		if (index === undefined || index < 0) {
			return undefined;
		}
		const { portalPageSectionItem } = this.props;
		const elements = portalPageSectionItem.elements;
		if (!elements || !Array.isArray(elements) || !elements[index]) {
			return undefined;
		}
		return (portalPageSectionItem.elements[index] as ISectionTextElement).text || undefined;
	}

	private getItemId(): string | undefined {
		const entityId = this.getItemEntityId();
		if (entityId) {
			try {
				const parsed = JSON.parse(entityId) as { type: string; id: string };
				if (parsed.id) {
					return parsed.id;
				}
			} catch (e) {
				console.error(e);
			}
		}
		const { portalPageSectionItem } = this.props;
		switch (portalPageSectionItem.type) {
			case c.PORTAL_PAGE_SECTION_ITEM_PROFILE:
				return this.getSectionText(4);
			case c.PORTAL_PAGE_SECTION_ITEM_SHOP:
			case c.PORTAL_PAGE_SECTION_ITEM_PRODUCT:
				return this.getSectionText(7);
			default:
				return undefined;
		}
	}

	private getItemEntityId(): string | undefined {
		const { portalPageSectionItem } = this.props;
		switch (portalPageSectionItem.type) {
			case c.PORTAL_PAGE_SECTION_ITEM_PROFILE:
			case c.PORTAL_PAGE_SECTION_ITEM_PRODUCT:
				return this.getSectionText(6);
			case c.PORTAL_PAGE_SECTION_ITEM_SHOP:
				return this.getSectionText(8);
			default:
				return undefined;
		}
	}

	private getFollowProps() {
		const { followItem, unfollowItem, userPreferences } = this.props;
		const type = this.getFollowType();
		const id = this.getItemId();
		if (!type || !id) {
			return {};
		}
		if (userPreferences && userPreferences.id === id && type === 'User') {
			return {};
		}
		return {
			isFollowing: this.getIsFollowing(),
			followItem: followItem ? () => followItem(type, id) : undefined,
			unfollowItem: unfollowItem ? () => unfollowItem(type, id) : undefined,
		};
	}

	private getIsFollowing() {
		const type = this.getFollowType();
		if (!type) {
			return undefined;
		}
		const followingItems = this.props.followingItems;
		if (!followingItems || !followingItems.length) {
			return undefined;
		}
		const itemId = this.getItemId();
		const entityId = this.getItemEntityId();
		for (const item of followingItems) {
			if (entityId && item.entityId === entityId) {
				return item.following;
			}
			try {
				const parsed = JSON.parse(item.entityId) as { type: string; id: string };
				if (
					parsed.id &&
					parsed.id === itemId &&
					typeof parsed.type === 'string' &&
					parsed.type.toLowerCase() === type.toLowerCase()
				) {
					return item.following;
				}
			} catch (e) {
				console.error(e);
				continue;
			}
		}
		return undefined;
	}

	private goToCard(id: string, type: string, target: string) {
		goToCard(this.props.dispatch, id, type, target);
	}

	private getComponent(
		portalPageSectionItem: IPortalPageSectionItem,
		isLastItemInRow: boolean,
		isFirstItemInRow: boolean,
		sectionType: string,
		isDragging: boolean,
	): JSX.Element {
		if (!portalPageSectionItem) {
			return null;
		}

		let component = null;

		switch (portalPageSectionItem.type) {
			case c.PORTAL_PAGE_SECTION_ITEM_ARTICLE:
				component = (
					<ViewSectionItemArticle
						key={'viewsectionitemarticle_' + Date.now}
						sectionItem={portalPageSectionItem}
						isLastItemInRow={isLastItemInRow}
						isDragging={isDragging}
						isFirstItemInRow={isFirstItemInRow}
						sectionType={sectionType}
					/>
				);
				break;
			case c.PORTAL_PAGE_SECTION_ITEM_POLL:
				component = (
					<ViewSectionItemPoll
						key={'viewsectionitempoll_' + Date.now}
						sectionItem={portalPageSectionItem}
						isLastItemInRow={isLastItemInRow}
					/>
				);
				break;
			case c.PORTAL_PAGE_SECTION_ITEM_PRODUCT:
				component = (
					<ViewSectionItemProduct
						key={'viewsectionitemproduct_' + Date.now}
						sectionItem={portalPageSectionItem}
						isLastItemInRow={isLastItemInRow}
						goToCard={this.goToCard}
						{...this.getFollowProps()}
					/>
				);
				break;
			case c.PORTAL_PAGE_SECTION_ITEM_PROFILE:
				component = (
					<ViewSectionItemProfile
						key={'viewsectionitemprofile_' + Date.now}
						sectionItem={portalPageSectionItem}
						isDragging={isDragging}
						{...this.getFollowProps()}
					/>
				);
				break;
			case c.PORTAL_PAGE_SECTION_ITEM_SHOP:
				component = (
					<ViewSectionItemShop
						key={'viewsectionitemshop_' + Date.now}
						sectionItem={portalPageSectionItem}
						isDragging={isDragging}
						{...this.getFollowProps()}
					/>
				);
				break;
			default:
				component = null;
		}

		return component;
	}

	public render() {
		const { portalPageSectionItem, isLastItemInRow, isFirstItemInRow, sectionType, isDragging } = this.props;

		return this.getComponent(portalPageSectionItem, isLastItemInRow, isFirstItemInRow, sectionType, isDragging);
	}
}

export { PortalPageSectionItemRenderer };
